import React, { FC, useState } from 'react'
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { colorValue } from '@moonpig/launchpad-theme'
import { useIsMobile } from '@moonpig/web-shared-utils'
import {
  ModuleContext,
  Module,
  ModuleSimplifiedTabs,
  Tab as TabType,
} from '../../types'
import { PageTypeEnum } from '../../../types'
import { useProductStore } from '../../../contexts/product'
import { E_CARD_SIZE_ID } from '../../../constants'

const fullHeightColumnStyles = s({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
})

const SimplifiedTabListWrapper = styled.div<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  max-height: ${({ isVisible }) => (isVisible ? '80px' : '0')};
  margin-bottom: 0;
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  transition:
    opacity 200ms ease,
    max-height 200ms ease,
    visibility 0ms linear ${({ isVisible }) => (isVisible ? '0ms' : '200ms')};
`

const SimplifiedTabList = styled(TabList)`
  &&,
  &&:focus-within {
    box-shadow: none;
    filter: none;
  }

  ${s({
    justifyContent: 'flex-start',
    gap: 12,
    paddingX: 4,
    paddingTop: 7,
    marginBottom: { xs: 0, md: 3 },
  })}
`

const StyledTabPanels = styled(TabPanels)`
  background-color: inherit;
`

const StyledScrollableTabs = styled(Tabs)`
  ${fullHeightColumnStyles}
`

const StyledScrollableTabPanels = styled(StyledTabPanels)`
  overflow-y: auto;
  ${s({
    flex: 1,
    marginTop: { xs: 0, md: 1 },
    minHeight: 0,
  })}
`

const StyledTabsWrapper = styled(Box)`
  ${fullHeightColumnStyles}
`

/**
 * Default Launchpad tab uses `bgcolor` on the trigger (often emitted as
 * `background` shorthand) and tinted hovers; reset with `background: transparent`
 * and `&&` so our rules win over the design-system tab styles.
 */
const SimplifiedTab = styled(Tab)`
  && {
    background: transparent;
  }

  &&[aria-selected='true'],
  &&[aria-selected='true']:hover {
    background: transparent;
  }

  &&[aria-selected='false']:hover {
    background: transparent;
  }

  &&:focus,
  &&:focus-within,
  &&:active,
  &&:focus-visible {
    box-shadow: none;
    background: transparent;
  }

  &&:focus-within:focus-visible {
    outline: 2px solid ${colorValue('colorInteractionFocusIndicator')};
    outline-offset: 2px;
  }
`

type Props = {
  module: ModuleSimplifiedTabs
  context: ModuleContext
  mapModule: (module: Module, props: ModuleContext) => React.JSX.Element | null
  onChanged?: (tab: TabType, index: number) => void
  'data-testid'?: string
}

export const SimplifiedTabsModule: FC<Props> = ({
  module: { tabs },
  context,
  mapModule,
  onChanged,
  'data-testid': testId = 'module-simplified-tabs',
}) => {
  const [selectedValue, setSelectedValue] = useState(() => tabs[0]?.title)
  const isMobile = useIsMobile()
  const [{ variant }] = useProductStore()
  const hasTitleAboveTabs = context.pageType === PageTypeEnum.PDP
  const isPhysicalCard = variant.key !== E_CARD_SIZE_ID

  const handleValueChange = (value: string) => {
    const index = tabs.findIndex(tab => tab.title === value)
    if (value !== selectedValue) {
      setSelectedValue(value)
      onChanged?.(tabs[index], index)
    }
  }

  const layoutTopMargin = isMobile && hasTitleAboveTabs ? -7 : 0

  return (
    <StyledTabsWrapper data-testid={testId} marginTop={layoutTopMargin}>
      <StyledScrollableTabs
        value={selectedValue}
        onValueChange={handleValueChange}
      >
        <SimplifiedTabListWrapper
          isVisible={isPhysicalCard}
          aria-hidden={!isPhysicalCard}
        >
          <SimplifiedTabList>
            {tabs.map(tab => (
              <SimplifiedTab
                key={tab.title}
                value={tab.title}
                aria-label={tab.label}
              >
                {tab.title}
              </SimplifiedTab>
            ))}
          </SimplifiedTabList>
        </SimplifiedTabListWrapper>
        <StyledScrollableTabPanels>
          {tabs.map(tab => (
            <TabPanel key={tab.title} value={tab.title}>
              <Box px={{ xs: 5, md: 8 }} py={{ xs: 5, md: 8 }}>
                {tab.modules.map((m, key) =>
                  mapModule(m as Module, { ...context, key }),
                )}
              </Box>
            </TabPanel>
          ))}
        </StyledScrollableTabPanels>
      </StyledScrollableTabs>
    </StyledTabsWrapper>
  )
}
